// src/component/page/airdrops/AirdropBubble.jsx
// Player Airdrops — frontend completo separado del casino.
// Conecta con su propio backend:
// GET  /api/player-airdrops/status
// GET  /api/player-airdrops/history
// GET  /api/player-airdrops/receipt/:airdropId
// POST /api/player-airdrops/create
// POST /api/player-airdrops/join
// POST /api/player-airdrops/cancel
// POST /api/player-airdrops/settle-expired

import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { keyframes } from "@mui/system";

/* ============================================================
   CONFIG
============================================================ */

const URL_MAIN =
  process.env.NODE_ENV === "production"
    ? window.location.origin
    : "http://localhost:3002";

    
const LANGUAGE_STORAGE_KEY = "bdb_language";

function normalizeLanguage(value) {
  return value === "es" ? "es" : "en";
}

function L(language, englishText, spanishText) {
  return language === "es" ? spanishText : englishText;
}

function localeFor(language) {
  return language === "es" ? "es-ES" : "en-US";
}

const AIRDROP_API = Object.freeze({
  status: `${URL_MAIN}/api/player-airdrops/status`,
  history: `${URL_MAIN}/api/player-airdrops/history`,
  receipt: (airdropId) =>
    `${URL_MAIN}/api/player-airdrops/receipt/${encodeURIComponent(airdropId)}`,
  create: `${URL_MAIN}/api/player-airdrops/create`,
  join: `${URL_MAIN}/api/player-airdrops/join`,
  cancel: `${URL_MAIN}/api/player-airdrops/cancel`,
  settleExpired: `${URL_MAIN}/api/player-airdrops/settle-expired`,
});

/* ============================================================
   HELPERS
============================================================ */

function getCookie(name) {
  if (typeof document === "undefined") return "";
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}

function getToken() {
  if (typeof window === "undefined") return "";
  return (
    getCookie("token") ||
    window.sessionStorage?.getItem("token") ||
    window.localStorage?.getItem("token") ||
    ""
  );
}

function authHeaders() {
  return { token: getToken() };
}

function requestId(prefix = "airdrop") {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return `${prefix}-${window.crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function fmt(value, decimals = 3) {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "0";

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(number);
}

function shortId(value = "") {
  const text = String(value || "");
  if (text.length <= 16) return text;
  return `${text.slice(0, 8)}...${text.slice(-6)}`;
}

function messageFromError(error, language = "en") {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    L(
      language,
      "The airdrop action could not be completed.",
      "No se pudo completar la acción del airdrop.",
    )
  );
}

function timeLeftLabel(value, language = "en") {
  if (!value) return "—";

  const end = new Date(value).getTime();
  const diff = Math.max(0, end - Date.now());

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${Math.ceil(diff / 1000)}s`;
}

function durationHelper(ms, language = "en") {
  const minutes = Math.round(Number(ms || 0) / 60000);
  if (!minutes) return "—";
  if (minutes < 60) {
    return `${minutes} ${L(language, "minutes", "minutos")}`;
  }

  const hours = minutes / 60;
  if (hours < 24) {
    return `${Math.round(hours)} ${L(language, "hours", "horas")}`;
  }

  return `${Math.round(hours / 24)} ${L(language, "days", "días")}`;
}

function slotUsagePercentage(drop) {
  const max = Number(drop?.maxParticipants || 0);
  const count = Number(drop?.participantCount || 0);
  if (!max || max <= 0) return 0;
  return Math.max(0, Math.min(100, (count / max) * 100));
}

function statusInfo(status, language = "en") {
  const value = String(status || "").toUpperCase();

  if (value === "COMPLETED") {
    return {
      label: L(language, "✅ PAID / SETTLED", "✅ PAGADO / LIQUIDADO"),
      shortLabel: L(language, "PAID", "PAGADO"),
      color: "#73ffbd",
      bg: "rgba(115,255,189,.12)",
      border: "rgba(115,255,189,.32)",
    };
  }

  if (value === "ACTIVE") {
    return {
      label: L(language, "⏳ ACTIVE", "⏳ ACTIVO"),
      shortLabel: L(language, "ACTIVE", "ACTIVO"),
      color: "#ffd45d",
      bg: "rgba(255,212,93,.12)",
      border: "rgba(255,212,93,.32)",
    };
  }

  if (value === "SETTLING") {
    return {
      label: L(language, "🔄 SETTLING", "🔄 LIQUIDANDO"),
      shortLabel: L(language, "SETTLING", "LIQUIDANDO"),
      color: "#79e8ff",
      bg: "rgba(121,232,255,.12)",
      border: "rgba(121,232,255,.32)",
    };
  }

  if (value === "CANCELLED") {
    return {
      label: L(language, "↩️ CANCELLED / REFUNDED", "↩️ CANCELADO / REEMBOLSADO"),
      shortLabel: L(language, "REFUNDED", "REEMBOLSADO"),
      color: "#ffb86c",
      bg: "rgba(255,184,108,.12)",
      border: "rgba(255,184,108,.32)",
    };
  }

  if (value === "ERROR") {
    return {
      label: L(language, "⚠️ ERROR", "⚠️ ERROR"),
      shortLabel: L(language, "ERROR", "ERROR"),
      color: "#ff8a8a",
      bg: "rgba(255,80,80,.12)",
      border: "rgba(255,80,80,.32)",
    };
  }

  return {
    label: value || L(language, "UNKNOWN", "DESCONOCIDO"),
    shortLabel: value || L(language, "UNKNOWN", "DESCONOCIDO"),
    color: "#ffffff",
    bg: "rgba(255,255,255,.08)",
    border: "rgba(255,255,255,.18)",
  };
}

/* ============================================================
   ANIMATIONS
============================================================ */

const neonPulse = keyframes`
  0%,100% { box-shadow: 0 0 14px rgba(255,183,0,.48), 0 0 35px rgba(126,55,255,.22); }
  50% { box-shadow: 0 0 28px rgba(255,183,0,.95), 0 0 66px rgba(126,55,255,.55); }
`;

const floatCard = keyframes`
  0%,100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const shine = keyframes`
  0% { transform: translateX(-180%) skewX(-24deg); }
  60%,100% { transform: translateX(280%) skewX(-24deg); }
`;

const slotBounce = keyframes`
  0%,100% { transform: translateY(0) rotate(0deg); }
  35% { transform: translateY(-10px) rotate(-4deg); }
  70% { transform: translateY(3px) rotate(3deg); }
`;

const subtleGlow = keyframes`
  0%,100% { border-color: rgba(255,255,255,.12); }
  50% { border-color: rgba(255,221,100,.38); }
`;

/* ============================================================
   SMALL UI
============================================================ */

function GlassPanel({ children, sx = {} }) {
  return (
    <Paper
      elevation={0}
      sx={{
        color: "white",
        border: "1px solid rgba(255,255,255,.12)",
        background:
          "linear-gradient(145deg,rgba(255,255,255,.075),rgba(255,255,255,.02))",
        backdropFilter: "blur(14px)",
        borderRadius: "22px",
        animation: `${subtleGlow} 4s ease-in-out infinite`,
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

function MetricBox({ label, value, accent = "#ffd45d", sub }) {
  return (
    <Box
      sx={{
        p: 1.1,
        borderRadius: "14px",
        border: "1px solid rgba(255,255,255,.08)",
        background: "rgba(255,255,255,.04)",
        minWidth: 0,
      }}
    >
      <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,.5)" }}>
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 0.2,
          fontWeight: 1000,
          color: accent,
          overflowWrap: "anywhere",
        }}
      >
        {value}
      </Typography>
      {sub ? (
        <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,.42)" }}>
          {sub}
        </Typography>
      ) : null}
    </Box>
  );
}

function HelpLine({ children }) {
  return (
    <Typography
      sx={{
        position: "relative",
        pl: 2,
        mb: 0.65,
        color: "rgba(255,255,255,.72)",
        fontSize: 13,
        lineHeight: 1.65,
        "&::before": {
          content: '"•"',
          position: "absolute",
          left: 0,
          color: "#ffd45d",
          fontWeight: 1000,
        },
      }}
    >
      {children}
    </Typography>
  );
}

function HelpSection({ icon, title, accent = "#ffd45d", children }) {
  return (
    <GlassPanel
      sx={{
        p: { xs: 1.5, sm: 2 },
        height: "100%",
        borderColor: `${accent}55`,
        background: `linear-gradient(145deg,${accent}14,rgba(5,3,10,.9))`,
      }}
    >
      <Typography sx={{ mb: 1, color: accent, fontSize: 16, fontWeight: 1000 }}>
        {icon} {title}
      </Typography>
      {children}
    </GlassPanel>
  );
}

/* ============================================================
   HELP DIALOG
============================================================ */

function AirdropHelpDialog({ open, onClose, language }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          color: "white",
          borderRadius: "24px",
          border: "2px solid rgba(255,210,80,.85)",
          background:
            "radial-gradient(circle at top,rgba(255,170,0,.18),transparent 36%),linear-gradient(180deg,#170b20,#050309)",
          boxShadow: "0 0 55px rgba(255,190,40,.35)",
        },
      }}
    >
      <DialogTitle sx={{ pr: 7 }}>
        <Typography sx={{ color: "#ffd45d", fontSize: 24, fontWeight: 1000 }}>
          {L(language, "❓ PLAYER AIRDROPS HELP", "❓ AYUDA DE AIRDROPS DE JUGADORES")}
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,.6)", fontSize: 13 }}>
          {L(
            language,
            "Community drops created with the creator's own game resources.",
            "Airdrops comunitarios creados con los recursos del propio creador.",
          )}
        </Typography>

        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 10, color: "white" }}
          aria-label={L(language, "Close help", "Cerrar ayuda")}
        >
          ✕
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: "rgba(255,255,255,.1)" }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <HelpSection icon="🎁" title={L(language, "WHAT IS THIS?", "¿QUÉ ES ESTO?")} accent="#ffd45d">
              <HelpLine>
                {L(
                  language,
                  "A real player creates the drop using resources already owned in Big Dog Bone or City Farm.",
                  "Un jugador real crea el airdrop usando recursos que ya posee en Big Dog Bone o City Farm.",
                )}
              </HelpLine>
              <HelpLine>
                {L(
                  language,
                  "The game does not create free rewards. It only locks and distributes what the creator deposits.",
                  "El juego no crea recompensas gratis. Solo bloquea y distribuye lo que deposita el creador.",
                )}
              </HelpLine>
              <HelpLine>
                {L(
                  language,
                  "All rewards are distributed when the countdown ends.",
                  "Todas las recompensas se distribuyen cuando termina la cuenta regresiva.",
                )}
              </HelpLine>
            </HelpSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <HelpSection icon="🔐" title={L(language, "SECURITY RULES", "REGLAS DE SEGURIDAD")} accent="#79e8ff">
              <HelpLine>
                {L(
                  language,
                  "The selected resource is deducted from the creator when the drop is created.",
                  "El recurso seleccionado se descuenta al creador cuando se crea el airdrop.",
                )}
              </HelpLine>
              <HelpLine>
                {L(
                  language,
                  "The creator cannot join their own drop.",
                  "El creador no puede unirse a su propio airdrop.",
                )}
              </HelpLine>
              <HelpLine>
                {L(
                  language,
                  "Each player can join only once per airdrop.",
                  "Cada jugador puede unirse una sola vez por airdrop.",
                )}
              </HelpLine>
              <HelpLine>
                {L(
                  language,
                  "Repeated clicks use a request ID to reduce duplicate actions.",
                  "Los clics repetidos usan un ID de solicitud para reducir acciones duplicadas.",
                )}
              </HelpLine>
            </HelpSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <HelpSection icon="⚖️" title={L(language, "EQUAL SPLIT", "DIVISIÓN EQUITATIVA")} accent="#73ffbd">
              <HelpLine>
                {L(
                  language,
                  "Final reward = total amount ÷ final number of participants.",
                  "Recompensa final = cantidad total ÷ número final de participantes.",
                )}
              </HelpLine>
              <HelpLine>
                {L(
                  language,
                  "Example: 1,000 Water and 25 participants = 40 Water each.",
                  "Ejemplo: 1.000 de agua y 25 participantes = 40 de agua para cada uno.",
                )}
              </HelpLine>
              <HelpLine>
                {L(
                  language,
                  "Players join first. Payment happens only when the timer ends.",
                  "Los jugadores se unen primero. El pago ocurre cuando termina el temporizador.",
                )}
              </HelpLine>
            </HelpSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <HelpSection icon="🧾" title={L(language, "RECEIPT / AUDIT", "RECIBO / AUDITORÍA")} accent="#c18aff">
              <HelpLine>
                {L(
                  language,
                  "After a drop is settled, open the receipt to confirm if it paid.",
                  "Después de liquidar un airdrop, abre el recibo para confirmar si pagó.",
                )}
              </HelpLine>
              <HelpLine>
                {L(
                  language,
                  "The receipt shows debit, payments, refunds, participants and balance before/after.",
                  "El recibo muestra débito, pagos, reembolsos, participantes y saldo antes/después.",
                )}
              </HelpLine>
              <HelpLine>
                {L(
                  language,
                  "If nobody joined, the full escrow returns to the creator.",
                  "Si nadie se unió, el depósito completo regresa al creador.",
                )}
              </HelpLine>
            </HelpSection>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   ACTIVE CARD
============================================================ */

function AirdropActiveCard({ drop, busy, onJoin, onCancel, language }) {
  const joined = Boolean(drop.joinedByViewer);
  const active = drop.status === "ACTIVE";
  const isCreator = Boolean(drop.creator && drop.creator === drop.viewerName);
  const canJoin = Boolean(drop.canJoin) && !joined && !isCreator;
  const slotsPct = slotUsagePercentage(drop);

  return (
    <GlassPanel
      sx={{
        p: 1.35,
        borderColor: joined
          ? "rgba(115,255,189,.42)"
          : "rgba(255,211,78,.2)",
        background:
          "linear-gradient(145deg,rgba(255,210,80,.09),rgba(5,3,10,.88))",
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 1000, color: "#ffd45d", fontSize: 15 }}>
            {drop.rewardIcon || "🎁"} {drop.creator}{" "}
            {L(language, "dropped", "repartió")}{" "}
            {fmt(drop.totalAmount, drop.decimals)} {drop.rewardLabel}
          </Typography>

          {drop.message ? (
            <Typography
              sx={{
                mt: 0.2,
                color: "rgba(255,255,255,.62)",
                fontSize: 12,
                overflowWrap: "anywhere",
              }}
            >
              “{drop.message}”
            </Typography>
          ) : null}
        </Box>

        <Chip
          size="small"
          label={`⏳ ${timeLeftLabel(drop.expiresAt, language)}`}
          sx={{
            color: "#fff",
            fontWeight: 900,
            bgcolor: "rgba(255,255,255,.08)",
            flexShrink: 0,
          }}
        />
      </Stack>

      <Box sx={{ mt: 1 }}>
        <LinearProgress
          variant={drop.maxParticipants ? "determinate" : "indeterminate"}
          value={slotsPct || 0}
          sx={{
            height: 8,
            borderRadius: 999,
            bgcolor: "rgba(255,255,255,.06)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 999,
              background: "linear-gradient(90deg,#ffd75b,#ff8c00,#ff57d2)",
            },
          }}
        />
      </Box>

      <Box
        sx={{
          mt: 1,
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr 1fr",
            sm: "repeat(4, minmax(0, 1fr))",
          },
          gap: 0.8,
        }}
      >
        <MetricBox
          label={L(language, "PARTICIPANTS", "PARTICIPANTES")}
          value={`👥 ${fmt(drop.participantCount, 0)}${
            drop.maxParticipants ? ` / ${fmt(drop.maxParticipants, 0)}` : ""
          }`}
          accent="#ffffff"
        />
        <MetricBox
          label={L(language, "CURRENT EACH", "ACTUAL POR PERSONA")}
          value={fmt(drop.estimatedPerParticipantCurrent, drop.decimals)}
          accent="#73ffbd"
        />
        <MetricBox
          label={L(language, "IF YOU JOIN", "SI TE UNES")}
          value={fmt(drop.estimatedPerParticipantIfYouJoin, drop.decimals)}
          accent="#79e8ff"
        />
        <MetricBox label={L(language, "TYPE", "TIPO")} value={drop.rewardLabel} accent="#ffd45d" />
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button
          fullWidth
          variant="contained"
          disabled={busy || !canJoin}
          onClick={() => onJoin(drop)}
          sx={{
            py: 1,
            color: "#120900",
            fontWeight: 1000,
            background: joined
              ? "linear-gradient(90deg,#73ffbd,#d9ff6f)"
              : "linear-gradient(90deg,#ffdf5b,#ff9d00)",
            "&:disabled": {
              color: "rgba(255,255,255,.55)",
              background: "rgba(255,255,255,.08)",
            },
          }}
        >
          {joined
            ? L(language, "✅ JOINED", "✅ UNIDO")
            : isCreator
              ? L(language, "YOUR DROP", "TU AIRDROP")
              : !active
                ? L(language, "ENDED", "FINALIZADO")
                : drop.remainingSlots === 0
                  ? L(language, "FULL", "LLENO")
                  : L(language, "JOIN DROP", "UNIRSE")}
        </Button>

        {isCreator && drop.participantCount === 0 ? (
          <Tooltip title={L(language, "Only the creator can cancel before anyone joins.", "Solo el creador puede cancelar antes de que alguien se una.")}>
            <span>
              <Button
                variant="outlined"
                disabled={busy}
                onClick={() => onCancel(drop)}
                sx={{
                  color: "#ff9a9a",
                  borderColor: "rgba(255,120,120,.35)",
                  fontWeight: 900,
                }}
              >
                {L(language, "CANCEL", "CANCELAR")}
              </Button>
            </span>
          </Tooltip>
        ) : null}
      </Stack>
    </GlassPanel>
  );
}

/* ============================================================
   HISTORY CARD
============================================================ */

function DropHistoryCard({ drop, onOpenReceipt, language }) {
  const info = statusInfo(drop.status, language);

  return (
    <GlassPanel
      sx={{
        p: 1.25,
        borderColor: info.border,
        background: `linear-gradient(145deg,${info.bg},rgba(5,3,10,.82))`,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 1000, color: "#ffd45d", fontSize: 14 }}>
            {drop.rewardIcon || "🎁"} {drop.creator}{" "}
            {L(language, "dropped", "repartió")}{" "}
            {fmt(drop.totalAmount, drop.decimals)} {drop.rewardLabel}
          </Typography>

          <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 11 }}>
            {L(language, "Created", "Creado")}:{" "}
            {drop.createdAt
              ? new Date(drop.createdAt).toLocaleString(localeFor(language))
              : "—"}
            {drop.settledAt
              ? ` · ${L(language, "Settled", "Liquidado")}: ${new Date(
                  drop.settledAt,
                ).toLocaleString(localeFor(language))}`
              : ""}
          </Typography>
        </Box>

        <Chip
          size="small"
          label={info.label}
          sx={{
            color: info.color,
            bgcolor: info.bg,
            border: `1px solid ${info.border}`,
            fontWeight: 1000,
            alignSelf: { xs: "flex-start", sm: "center" },
          }}
        />
      </Stack>

      <Box
        sx={{
          mt: 1,
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr 1fr",
            sm: "repeat(4, minmax(0, 1fr))",
          },
          gap: 0.75,
        }}
      >
        <MetricBox
          label={L(language, "PARTICIPANTS", "PARTICIPANTES")}
          value={`👥 ${fmt(drop.participantCount, 0)}`}
          accent="#ffffff"
        />
        <MetricBox
          label={L(language, "EACH PAID", "PAGO POR PERSONA")}
          value={`${fmt(drop.perParticipantAmount, drop.decimals)}`}
          accent="#73ffbd"
        />
        <MetricBox
          label={L(language, "DISTRIBUTED", "DISTRIBUIDO")}
          value={fmt(drop.distributedAmount, drop.decimals)}
          accent="#79e8ff"
        />
        <MetricBox
          label={L(language, "REFUNDED", "REEMBOLSADO")}
          value={fmt(drop.refundedAmount, drop.decimals)}
          accent="#ffcf66"
        />
      </Box>

      <Button
        fullWidth
        variant="outlined"
        onClick={() => onOpenReceipt(drop.airdropId)}
        sx={{
          mt: 1,
          color: "#73ffbd",
          borderColor: "rgba(115,255,189,.32)",
          fontWeight: 1000,
        }}
      >
        {L(language, "🧾 VIEW RECEIPT / AUDIT", "🧾 VER RECIBO / AUDITORÍA")}
      </Button>
    </GlassPanel>
  );
}

/* ============================================================
   RECEIPT DIALOG
============================================================ */

function AirdropReceiptDialog({ open, receipt, loading, onClose, language }) {
  const drop = receipt?.airdrop || {};
  const summary = receipt?.summary || {};
  const participants = receipt?.participants || [];
  const ledger = receipt?.ledger || [];
  const info = statusInfo(summary.status || drop.status, language);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          color: "white",
          borderRadius: "24px",
          border: "2px solid rgba(115,255,189,.55)",
          background:
            "radial-gradient(circle at top,rgba(115,255,189,.14),transparent 38%),linear-gradient(180deg,#111021,#050309)",
          boxShadow: "0 0 55px rgba(115,255,189,.22)",
        },
      }}
    >
      <DialogTitle sx={{ pr: 7 }}>
        <Typography sx={{ color: "#73ffbd", fontSize: 24, fontWeight: 1000 }}>
          {L(language, "🧾 AIRDROP RECEIPT", "🧾 RECIBO DEL AIRDROP")}
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,.62)", fontSize: 13 }}>
          {L(
            language,
            "Full audit: debit, payment, refund and balance before/after.",
            "Auditoría completa: débito, pago, reembolso y saldo antes/después.",
          )}
        </Typography>

        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 10, color: "white" }}
          aria-label={L(language, "Close receipt", "Cerrar recibo")}
        >
          ✕
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: "rgba(255,255,255,.1)" }}>
        {loading ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 7 }}>
            <CircularProgress sx={{ color: "#73ffbd" }} />
            <Typography sx={{ color: "rgba(255,255,255,.65)" }}>
              {L(language, "Loading receipt...", "Cargando recibo...")}
            </Typography>
          </Stack>
        ) : receipt ? (
          <Stack spacing={1.5}>
            <GlassPanel
              sx={{
                p: 1.5,
                borderColor: info.border,
                background: `linear-gradient(145deg,${info.bg},rgba(5,3,10,.86))`,
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                spacing={1.2}
              >
                <Box>
                  <Typography
                    sx={{ fontWeight: 1000, color: "#ffd45d", fontSize: 18 }}
                  >
                    {drop.rewardIcon || "🎁"} {drop.creator}{" "}
                    {L(language, "dropped", "repartió")}{" "}
                    {fmt(drop.totalAmount, drop.decimals)} {drop.rewardLabel}
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 12 }}>
                    {L(language, "Airdrop ID", "ID del airdrop")}: {shortId(drop.airdropId)}
                  </Typography>
                </Box>

                <Chip
                  label={info.label}
                  sx={{
                    color: info.color,
                    bgcolor: info.bg,
                    border: `1px solid ${info.border}`,
                    fontWeight: 1000,
                    alignSelf: { xs: "flex-start", md: "center" },
                  }}
                />
              </Stack>
            </GlassPanel>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr 1fr",
                  md: "repeat(6, minmax(0, 1fr))",
                },
                gap: 1,
              }}
            >
              <MetricBox
                label={L(language, "CREATOR DEBIT", "DÉBITO DEL CREADOR")}
                value={fmt(summary.totalDebited, drop.decimals)}
                accent="#ffcf66"
              />
              <MetricBox
                label={L(language, "PARTICIPANTS", "PARTICIPANTES")}
                value={`👥 ${fmt(summary.participants, 0)}`}
                accent="#ffffff"
              />
              <MetricBox
                label={L(language, "PAID USERS", "USUARIOS PAGADOS")}
                value={fmt(summary.paidParticipants, 0)}
                accent="#73ffbd"
              />
              <MetricBox
                label={L(language, "EACH PAID", "PAGO POR PERSONA")}
                value={fmt(summary.perParticipantAmount, drop.decimals)}
                accent="#73ffbd"
              />
              <MetricBox
                label={L(language, "DISTRIBUTED", "DISTRIBUIDO")}
                value={fmt(summary.totalPaid || summary.distributedAmount, drop.decimals)}
                accent="#79e8ff"
              />
              <MetricBox
                label={L(language, "REFUNDED", "REEMBOLSADO")}
                value={fmt(summary.totalRefunded || summary.refundedAmount, drop.decimals)}
                accent="#ffb86c"
              />
            </Box>

            <GlassPanel sx={{ p: 1.5 }}>
              <Typography sx={{ mb: 1, color: "#73ffbd", fontWeight: 1000 }}>
                {L(language, "👥 Participants paid", "👥 Participantes pagados")}
              </Typography>

              {participants.length ? (
                <Box sx={{ overflowX: "auto" }}>
                  <Box
                    component="table"
                    sx={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 12,
                      "& th": {
                        textAlign: "left",
                        color: "rgba(255,255,255,.55)",
                        borderBottom: "1px solid rgba(255,255,255,.12)",
                        py: 0.8,
                      },
                      "& td": {
                        borderBottom: "1px solid rgba(255,255,255,.07)",
                        py: 0.8,
                        pr: 1,
                      },
                    }}
                  >
                    <thead>
                      <tr>
                        <th>{L(language, "User", "Usuario")}</th>
                        <th>{L(language, "Status", "Estado")}</th>
                        <th>{L(language, "Amount", "Cantidad")}</th>
                        <th>{L(language, "Field", "Campo")}</th>
                        <th>{L(language, "Before", "Antes")}</th>
                        <th>{L(language, "After", "Después")}</th>
                        <th>{L(language, "Paid At", "Pagado el")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {participants.map((p) => (
                        <tr key={p.nombre}>
                          <td style={{ color: "#ffd45d", fontWeight: 900 }}>
                            {p.nombre}
                          </td>
                          <td>{p.status}</td>
                          <td style={{ color: "#73ffbd", fontWeight: 900 }}>
                            {fmt(p.amount, drop.decimals)}
                          </td>
                          <td>{p.field || "—"}</td>
                          <td>
                            {p.balanceBefore === null || p.balanceBefore === undefined
                              ? "—"
                              : fmt(p.balanceBefore, drop.decimals)}
                          </td>
                          <td>
                            {p.balanceAfter === null || p.balanceAfter === undefined
                              ? "—"
                              : fmt(p.balanceAfter, drop.decimals)}
                          </td>
                          <td>
                            {p.paidAt
                              ? new Date(p.paidAt).toLocaleString(
                                  localeFor(language),
                                )
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Box>
                </Box>
              ) : (
                <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 13 }}>
                  {L(
                    language,
                    "No participants. If the airdrop ended, the full amount returns to the creator.",
                    "No hubo participantes. Si el airdrop terminó, la cantidad completa regresa al creador.",
                  )}
                </Typography>
              )}
            </GlassPanel>

            <GlassPanel sx={{ p: 1.5 }}>
              <Typography sx={{ mb: 1, color: "#79e8ff", fontWeight: 1000 }}>
                {L(language, "📒 Balance movements", "📒 Movimientos de saldo")}
              </Typography>

              {ledger.length ? (
                <Stack spacing={0.7}>
                  {ledger.map((row, index) => (
                    <Box
                      key={`${row.type}-${row.nombre}-${index}`}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: "1.2fr .9fr .8fr .8fr .8fr 1.3fr",
                        },
                        gap: 0.8,
                        p: 0.9,
                        borderRadius: "12px",
                        border: "1px solid rgba(255,255,255,.08)",
                        bgcolor: "rgba(255,255,255,.035)",
                        fontSize: 12,
                      }}
                    >
                      <Box>
                        <b>{row.type}</b>
                        <Typography
                          sx={{ color: "rgba(255,255,255,.45)", fontSize: 10 }}
                        >
                          {row.createdAt
                            ? new Date(row.createdAt).toLocaleString(
                                localeFor(language),
                              )
                            : ""}
                        </Typography>
                      </Box>
                      <Box>{row.nombre}</Box>
                      <Box>{row.field || "—"}</Box>
                      <Box
                        sx={{
                          color: Number(row.amount) < 0 ? "#ff9a9a" : "#73ffbd",
                          fontWeight: 900,
                        }}
                      >
                        {Number(row.amount) > 0 ? "+" : ""}
                        {fmt(row.amount, drop.decimals)}
                      </Box>
                      <Box>
                        {row.balanceBefore === null || row.balanceBefore === undefined
                          ? "—"
                          : fmt(row.balanceBefore, drop.decimals)}
                      </Box>
                      <Box>
                        {row.balanceAfter === null || row.balanceAfter === undefined
                          ? "—"
                          : fmt(row.balanceAfter, drop.decimals)}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 13 }}>
                  {L(language, "No ledger movements found yet.", "Todavía no se encontraron movimientos contables.")}
                </Typography>
              )}
            </GlassPanel>
          </Stack>
        ) : (
          <Typography sx={{ color: "rgba(255,255,255,.65)" }}>
            {L(language, "No receipt selected.", "No hay un recibo seleccionado.")}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   BOTTOM DOCK / BANNER
============================================================ */

function AirdropLeftDock({ activeCount, featuredDrop, onOpen, language }) {
  const hasActive = activeCount > 0;

  return (
    <Box
      sx={{
        position: "fixed",
        left: { xs: 8, md: 14 },
        top: { xs: 88, md: 82 },
        bottom: { xs: 92, md: 88 },
        width: { xs: 92, sm: 112, md: 132 },
        zIndex: 1350,
        pointerEvents: "none",
        display: "flex",
        alignItems: "center",
      }}
    >
      <GlassPanel
        sx={{
          pointerEvents: "auto",
          width: "100%",
          p: { xs: 0.8, md: 1 },
          borderRadius: "24px",
          border: hasActive
            ? "2px solid rgba(115,255,189,.72)"
            : "2px solid rgba(255,210,80,.55)",
          background: hasActive
            ? "radial-gradient(circle at top,rgba(115,255,189,.18),transparent 32%),radial-gradient(circle at bottom,rgba(255,90,210,.16),transparent 34%),linear-gradient(180deg,rgba(28,8,42,.96),rgba(60,25,9,.96),rgba(22,7,38,.96))"
            : "radial-gradient(circle at top,rgba(255,210,80,.14),transparent 32%),linear-gradient(180deg,rgba(28,8,42,.94),rgba(60,25,9,.94),rgba(22,7,38,.94))",
          boxShadow: hasActive
            ? "0 0 26px rgba(115,255,189,.32),0 0 58px rgba(255,180,30,.2)"
            : "0 0 22px rgba(255,180,30,.25)",
          animation: hasActive ? `${neonPulse} 1.8s ease-in-out infinite` : "none",
          position: "relative",
          overflow: "hidden",
          "&::after": {
            content: '""',
            position: "absolute",
            top: "-30%",
            left: "-80%",
            width: "70%",
            height: "160%",
            background:
              "linear-gradient(90deg,transparent,rgba(255,255,255,.28),transparent)",
            animation: `${shine} 4s ease-in-out infinite`,
            transform: "skewX(-20deg)",
          },
        }}
      >
        <Stack
          spacing={0.9}
          alignItems="center"
          sx={{ position: "relative", zIndex: 1 }}
        >
          <Box
            sx={{
              width: { xs: 54, md: 66 },
              height: { xs: 54, md: 66 },
              borderRadius: "20px",
              display: "grid",
              placeItems: "center",
              fontSize: { xs: 30, md: 38 },
              background: hasActive
                ? "linear-gradient(135deg,#73ffbd,#ffe66d,#ff8c00)"
                : "linear-gradient(135deg,#ffe66d,#ffac1c)",
              color: "#180800",
              boxShadow: hasActive
                ? "0 0 22px rgba(115,255,189,.48)"
                : "0 0 16px rgba(255,190,40,.28)",
              animation: hasActive ? `${slotBounce} 2.2s ease-in-out infinite` : "none",
            }}
          >
            🎁
          </Box>

          <Box sx={{ textAlign: "center", width: "100%" }}>
            <Typography
              sx={{
                color: "#ffd45d",
                fontSize: { xs: 11, md: 13 },
                fontWeight: 1000,
                lineHeight: 1.05,
              }}
            >
              {L(language, "PLAYER", "AIRDROPS")}
            </Typography>
            <Typography
              sx={{
                color: "#ffd45d",
                fontSize: { xs: 11, md: 13 },
                fontWeight: 1000,
                lineHeight: 1.05,
              }}
            >
              {L(language, "AIRDROPS", "DE JUGADORES")}
            </Typography>
          </Box>

          <Chip
            size="small"
            label={hasActive ? `${activeCount} ${L(language, "LIVE", "ACTIVOS")}` : L(language, "NO LIVE", "SIN ACTIVOS")}
            sx={{
              width: "100%",
              height: 24,
              color: hasActive ? "#00170d" : "#2c1400",
              bgcolor: hasActive ? "#73ffbd" : "#ffd45d",
              fontWeight: 1000,
              fontSize: { xs: 10, md: 11 },
              boxShadow: hasActive ? "0 0 14px rgba(115,255,189,.32)" : "none",
              "& .MuiChip-label": {
                px: 0.4,
              },
            }}
          />

          {featuredDrop ? (
            <Box
              sx={{
                width: "100%",
                p: 0.8,
                borderRadius: "14px",
                border: "1px solid rgba(255,255,255,.10)",
                background: "rgba(255,255,255,.045)",
                textAlign: "center",
              }}
            >
              <Typography sx={{ color: "#73ffbd", fontWeight: 1000, fontSize: 10 }}>
                {L(language, "LIVE DROP", "AIRDROP ACTIVO")}
              </Typography>
              <Typography
                sx={{
                  mt: 0.25,
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: { xs: 10, md: 11 },
                  lineHeight: 1.2,
                  overflowWrap: "anywhere",
                }}
              >
                {fmt(featuredDrop.totalAmount, featuredDrop.decimals)}
              </Typography>
              <Typography
                sx={{
                  color: "#ffd45d",
                  fontWeight: 1000,
                  fontSize: { xs: 9.5, md: 10.5 },
                  lineHeight: 1.15,
                  overflowWrap: "anywhere",
                }}
              >
                {featuredDrop.rewardLabel}
              </Typography>
              <Typography
                sx={{
                  mt: 0.45,
                  color: "rgba(255,255,255,.62)",
                  fontSize: { xs: 9, md: 10 },
                  lineHeight: 1.15,
                }}
              >
                ⏳ {timeLeftLabel(featuredDrop.expiresAt, language)}
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,.62)",
                  fontSize: { xs: 9, md: 10 },
                  lineHeight: 1.15,
                }}
              >
                👥 {fmt(featuredDrop.participantCount, 0)}
                {featuredDrop.maxParticipants
                  ? `/${fmt(featuredDrop.maxParticipants, 0)}`
                  : ""}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                p: 0.8,
                borderRadius: "14px",
                border: "1px dashed rgba(255,255,255,.14)",
                background: "rgba(255,255,255,.035)",
                textAlign: "center",
              }}
            >
              <Typography sx={{ color: "rgba(255,255,255,.68)", fontSize: 10, lineHeight: 1.2 }}>
                {L(
                  language,
                  "Create or join community drops.",
                  "Crea o únete a airdrops comunitarios.",
                )}
              </Typography>
            </Box>
          )}

          <Button
            fullWidth
            variant="contained"
            onClick={onOpen}
            sx={{
              minWidth: 0,
              px: 0.5,
              py: 0.9,
              color: "#160900",
              fontSize: { xs: 10, md: 11 },
              lineHeight: 1.1,
              fontWeight: 1000,
              borderRadius: "14px",
              background: hasActive
                ? "linear-gradient(90deg,#73ffbd,#eaff62,#ff9d00)"
                : "linear-gradient(90deg,#fff071,#ff9b00)",
              boxShadow: hasActive
                ? "0 0 18px rgba(115,255,189,.36)"
                : "0 0 12px rgba(255,190,40,.2)",
            }}
          >
            {hasActive ? L(language, "OPEN / JOIN", "ABRIR / UNIRSE") : L(language, "CREATE", "CREAR")}
          </Button>
        </Stack>
      </GlassPanel>
    </Box>
  );
}


/* ============================================================
   MAIN
============================================================ */

export default function AirdropBubble({ onNotice }) {
  const [language, setLanguage] = useState(() => {
    if (typeof window === "undefined") return "en";
    return normalizeLanguage(
      window.localStorage?.getItem(LANGUAGE_STORAGE_KEY),
    );
  });
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [internalNotice, setInternalNotice] = useState({
    open: false,
    type: "success",
    text: "",
  });

  const [rewardType, setRewardType] = useState("");
  const [totalAmount, setTotalAmount] = useState("");
  const [duration, setDuration] = useState("1h");
  const [maxParticipants, setMaxParticipants] = useState("");
  const [message, setMessage] = useState("");

  const changeLanguage = useCallback((nextLanguage) => {
    const normalized = normalizeLanguage(nextLanguage);
    setLanguage(normalized);

    if (typeof window !== "undefined") {
      window.localStorage?.setItem(LANGUAGE_STORAGE_KEY, normalized);
      window.dispatchEvent(
        new CustomEvent("bdb-language-change", {
          detail: { language: normalized },
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }
  }, [language]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncLanguage = (event) => {
      if (event?.type === "storage" && event.key !== LANGUAGE_STORAGE_KEY) {
        return;
      }

      const nextLanguage =
        event?.detail?.language ||
        window.localStorage?.getItem(LANGUAGE_STORAGE_KEY);

      setLanguage(normalizeLanguage(nextLanguage));
    };

    window.addEventListener("storage", syncLanguage);
    window.addEventListener("bdb-language-change", syncLanguage);

    return () => {
      window.removeEventListener("storage", syncLanguage);
      window.removeEventListener("bdb-language-change", syncLanguage);
    };
  }, []);

  const notify = useCallback(
    (text, type = "success") => {
      if (typeof onNotice === "function") {
        onNotice(text, type);
        return;
      }

      setInternalNotice({
        open: true,
        type,
        text,
      });
    },
    [onNotice],
  );

  const loadAirdrops = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);

      try {
        const [statusResponse, historyResponse] = await Promise.all([
          axios.get(AIRDROP_API.status, {
            headers: authHeaders(),
          }),
          axios
            .get(`${AIRDROP_API.history}?limit=12`, {
              headers: authHeaders(),
            })
            .catch(() => ({ data: { history: [] } })),
        ]);

        const data = statusResponse.data || {};
        setStatus(data);
        setHistory(historyResponse.data?.history || []);

        const firstEnabled = (data.rewardTypes || []).find(
          (type) => !type.disabled,
        );
        setRewardType((current) => current || firstEnabled?.id || "");
      } catch (error) {
        if (!silent) notify(messageFromError(error, language), "error");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [language, notify],
  );

  useEffect(() => {
    loadAirdrops(true);
    const timer = window.setInterval(() => loadAirdrops(true), 20000);
    return () => window.clearInterval(timer);
  }, [loadAirdrops]);

  useEffect(() => {
    if (open) loadAirdrops(true);
  }, [open, loadAirdrops]);

  const active = status?.active || [];
  const rewardTypes = (status?.rewardTypes || []).filter((type) => !type.disabled);
  const balances = status?.balances || {};
  const selectedType = rewardTypes.find((type) => type.id === rewardType);
  const selectedBalance = selectedType ? balances[selectedType.id] : null;
  const activeCount = active.length;
  const featuredDrop = active[0] || null;

  const estimatedSplits = useMemo(() => {
    if (!selectedType) return null;

    const amount = Number(totalAmount || 0);
    const participants = Number(maxParticipants || 0);

    if (
      !Number.isFinite(amount) ||
      amount <= 0 ||
      !Number.isFinite(participants) ||
      participants <= 0
    ) {
      return null;
    }

    const decimals = Number(selectedType.decimals || 0);
    const each =
      Math.floor((amount / participants) * 10 ** decimals) / 10 ** decimals;
    const used = each * participants;
    const remainder = Math.max(0, amount - used);

    return { each, remainder };
  }, [selectedType, totalAmount, maxParticipants]);

  const createDrop = async () => {
    if (busy) return;

    const amount = Number(totalAmount);

    if (!rewardType) {
      notify(L(language, "Choose what you want to drop.", "Elige lo que quieres repartir."), "warning");
      return;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      notify(L(language, "Enter a valid total amount.", "Ingresa una cantidad total válida."), "warning");
      return;
    }

    setBusy(true);

    try {
      const response = await axios.post(
        AIRDROP_API.create,
        {
          rewardType,
          totalAmount: amount,
          duration,
          maxParticipants: maxParticipants ? Number(maxParticipants) : 0,
          message,
          requestId: requestId("airdrop-create"),
        },
        { headers: authHeaders() },
      );

      setTotalAmount("");
      setMaxParticipants("");
      setMessage("");

      notify(
        `${L(language, "Airdrop created", "Airdrop creado")}: ${fmt(
          response.data?.airdrop?.totalAmount,
          response.data?.airdrop?.decimals,
        )} ${response.data?.airdrop?.rewardLabel || ""}`,
        "success",
      );

      await loadAirdrops(true);
    } catch (error) {
      notify(messageFromError(error, language), "error");
    } finally {
      setBusy(false);
    }
  };

  const joinDrop = async (drop) => {
    if (busy || !drop?.airdropId) return;

    setBusy(true);

    try {
      await axios.post(
        AIRDROP_API.join,
        {
          airdropId: drop.airdropId,
          requestId: requestId("airdrop-join"),
        },
        { headers: authHeaders() },
      );

      notify(
        L(
          language,
          "You joined the airdrop. Rewards are paid when the timer ends.",
          "Te uniste al airdrop. Las recompensas se pagan cuando termina el temporizador.",
        ),
        "success",
      );

      await loadAirdrops(true);
    } catch (error) {
      notify(messageFromError(error, language), "error");
    } finally {
      setBusy(false);
    }
  };

  const cancelDrop = async (drop) => {
    if (busy || !drop?.airdropId) return;

    setBusy(true);

    try {
      await axios.post(
        AIRDROP_API.cancel,
        { airdropId: drop.airdropId },
        { headers: authHeaders() },
      );

      notify(L(language, "Airdrop cancelled and escrow returned.", "Airdrop cancelado y depósito devuelto."), "info");
      await loadAirdrops(true);
    } catch (error) {
      notify(messageFromError(error, language), "error");
    } finally {
      setBusy(false);
    }
  };

  const loadReceipt = async (airdropId) => {
    if (!airdropId) return;

    setReceiptOpen(true);
    setReceiptLoading(true);

    try {
      const response = await axios.get(AIRDROP_API.receipt(airdropId), {
        headers: authHeaders(),
      });

      setSelectedReceipt(response.data);
    } catch (error) {
      notify(messageFromError(error, language), "error");
      setSelectedReceipt(null);
    } finally {
      setReceiptLoading(false);
    }
  };

  const settleExpired = async () => {
    if (busy) return;

    setBusy(true);

    try {
      const response = await axios.post(
        AIRDROP_API.settleExpired,
        { limit: 20 },
        { headers: authHeaders() },
      );

      const receipts = response.data?.receipts || [];

      notify(
        Number(response.data?.settled || 0) > 0
          ? `${response.data.settled} ${L(
              language,
              "expired airdrop(s) settled. Receipt opened.",
              "airdrop(s) vencido(s) liquidado(s). Recibo abierto.",
            )}`
          : L(
              language,
              "No expired airdrops to settle right now.",
              "No hay airdrops vencidos para liquidar ahora.",
            ),
        "info",
      );

      if (receipts.length) {
        setSelectedReceipt(receipts[0]);
        setReceiptOpen(true);
      }

      await loadAirdrops(true);
    } catch (error) {
      notify(messageFromError(error, language), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AirdropLeftDock
        activeCount={activeCount}
        featuredDrop={featuredDrop}
        onOpen={() => setOpen(true)}
        language={language}
      />

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            color: "white",
            borderRadius: { xs: "18px", sm: "28px" },
            border: "2px solid rgba(255,210,80,.9)",
            background:
              "radial-gradient(circle at top left,rgba(255,185,0,.22),transparent 34%),radial-gradient(circle at top right,rgba(255,70,214,.15),transparent 32%),linear-gradient(180deg,#170b22,#050309)",
            boxShadow:
              "0 0 35px rgba(255,190,40,.35),0 0 75px rgba(255,85,210,.2)",
            m: { xs: 1, sm: 2 },
            maxHeight: { xs: "96vh", sm: "94vh" },
          },
        }}
      >
        <DialogTitle sx={{ pr: 8 }}>
          <Typography
            sx={{
              color: "#ffd45d",
              fontSize: { xs: 23, sm: 31 },
              fontWeight: 1000,
            }}
          >
            {L(language, "🎁 PLAYER AIRDROPS", "🎁 AIRDROPS DE JUGADORES")}
          </Typography>

          <Typography sx={{ color: "rgba(255,255,255,.62)", fontSize: 13 }}>
            {L(
              language,
              "Create community drops using your own BDB or City Farm resources. Rewards split equally when the timer ends.",
              "Crea airdrops comunitarios usando tus propios recursos de BDB o City Farm. Las recompensas se dividen por igual cuando termina el temporizador.",
            )}
          </Typography>

          <Stack
            direction="row"
            spacing={0.7}
            useFlexGap
            flexWrap="wrap"
            sx={{ mt: 1.2 }}
          >
            <Button
              size="small"
              variant={language === "en" ? "contained" : "outlined"}
              onClick={() => changeLanguage("en")}
              aria-pressed={language === "en"}
              sx={{
                minWidth: 82,
                color: language === "en" ? "#160900" : "#ffd45d",
                borderColor: "rgba(255,212,93,.55)",
                bgcolor: language === "en" ? "#ffd45d" : "transparent",
                fontWeight: 1000,
              }}
            >
              English
            </Button>
            <Button
              size="small"
              variant={language === "es" ? "contained" : "outlined"}
              onClick={() => changeLanguage("es")}
              aria-pressed={language === "es"}
              sx={{
                minWidth: 82,
                color: language === "es" ? "#160900" : "#73ffbd",
                borderColor: "rgba(115,255,189,.55)",
                bgcolor: language === "es" ? "#73ffbd" : "transparent",
                fontWeight: 1000,
              }}
            >
              Español
            </Button>
          </Stack>

          <Stack
            direction="row"
            spacing={0.8}
            sx={{ position: "absolute", right: 12, top: 10 }}
          >
            <IconButton
              onClick={() => setHelpOpen(true)}
              sx={{ color: "#ffd45d", bgcolor: "rgba(255,255,255,.06)" }}
              aria-label={L(language, "Airdrop help", "Ayuda de airdrops")}
            >
              ?
            </IconButton>

            <IconButton
              onClick={() => setOpen(false)}
              sx={{ color: "white", bgcolor: "rgba(255,255,255,.06)" }}
              aria-label={L(language, "Close airdrops", "Cerrar airdrops")}
            >
              ✕
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: "rgba(255,255,255,.09)" }}>
          <Alert
            severity="info"
            sx={{
              mb: 1.5,
              color: "#dff8ff",
              bgcolor: "rgba(55,180,255,.09)",
              border: "1px solid rgba(90,210,255,.22)",
              "& .MuiAlert-icon": { color: "#6fe5ff" },
            }}
          >
            {language === "es" ? (
              <>
                <b>Cómo saber si fue pagado:</b> cuando termina el temporizador,
                el airdrop pasa de ACTIVO a HISTORIAL como{" "}
                <b>PAGADO / LIQUIDADO</b>. Abre el recibo para ver quién recibió
                recompensas, el saldo antes/después y cualquier reembolso.
              </>
            ) : (
              <>
                <b>How to know if it was paid:</b> when the timer ends, the drop
                moves from ACTIVE to HISTORY as <b>PAID / SETTLED</b>. Open the
                receipt to see who received rewards, balance before/after, and
                any refund.
              </>
            )}
          </Alert>

          <Grid container spacing={1.8}>
            <Grid item xs={12} lg={4.7}>
              <GlassPanel
                sx={{
                  p: 2,
                  borderColor: "rgba(255,211,78,.36)",
                  background:
                    "linear-gradient(145deg,rgba(255,180,30,.14),rgba(5,3,10,.92))",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1.1 }}
                >
                  <Box>
                    <Typography
                      sx={{ fontWeight: 1000, color: "#ffd45d", fontSize: 19 }}
                    >
                      {L(language, "+ CREATE AIRDROP", "+ CREAR AIRDROP")}
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 12 }}>
                      {L(
                        language,
                        "Select a reward, review your balance, then create the drop.",
                        "Selecciona una recompensa, revisa tu saldo y crea el airdrop.",
                      )}
                    </Typography>
                  </Box>

                  <Button
                    size="small"
                    onClick={() => loadAirdrops()}
                    disabled={loading}
                    sx={{ color: "#ffd45d", fontWeight: 900 }}
                  >
                    {L(language, "REFRESH", "ACTUALIZAR")}
                  </Button>
                </Stack>

                <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,.58)", mb: 0.8 }}>
                  {L(language, "1) Choose reward type", "1) Elige el tipo de recompensa")}
                </Typography>

                <Stack
                  direction="row"
                  spacing={0.7}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mb: 1.3, maxHeight: 160, overflowY: "auto", pr: 0.5 }}
                >
                  {rewardTypes.length ? (
                    rewardTypes.map((type) => {
                      const isActive = rewardType === type.id;
                      const typeBalance = balances?.[type.id];

                      return (
                        <Chip
                          key={type.id}
                          clickable
                          onClick={() => setRewardType(type.id)}
                          label={`${type.icon || "🎁"} ${type.label}`}
                          sx={{
                            color: isActive ? "#160900" : "white",
                            fontWeight: 950,
                            bgcolor: isActive ? "#ffd45d" : "rgba(255,255,255,.08)",
                            border: "1px solid rgba(255,255,255,.12)",
                            position: "relative",
                            "&::after": typeBalance?.available
                              ? {
                                  content: '""',
                                  position: "absolute",
                                  top: 4,
                                  right: 5,
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  bgcolor: "#73ffbd",
                                }
                              : {},
                          }}
                        />
                      );
                    })
                  ) : (
                    <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 13 }}>
                      {L(
                        language,
                        "No reward types available. Check backend connection.",
                        "No hay tipos de recompensa disponibles. Revisa la conexión con el backend.",
                      )}
                    </Typography>
                  )}
                </Stack>

                {selectedType ? (
                  <GlassPanel
                    sx={{
                      p: 1.2,
                      mb: 1.2,
                      borderColor: "rgba(110,224,255,.25)",
                      background:
                        "linear-gradient(145deg,rgba(53,169,255,.12),rgba(255,255,255,.02))",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 1000, color: "#79e8ff" }}>
                          {selectedType.icon} {selectedType.label}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,.58)" }}>
                          {L(language, "Min", "Mín.")}{" "}
                          {fmt(selectedType.minTotal, selectedType.decimals)} ·{" "}
                          {L(language, "Max", "Máx.")}{" "}
                          {fmt(selectedType.maxTotal, selectedType.decimals)}
                          {selectedType.integerOnly
                            ? L(
                                language,
                                " · Whole numbers only",
                                " · Solo números enteros",
                              )
                            : ""}
                        </Typography>
                      </Box>

                      <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                        <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,.5)" }}>
                          {L(language, "AVAILABLE BALANCE", "SALDO DISPONIBLE")}
                        </Typography>
                        <Typography sx={{ fontWeight: 1000, color: "#73ffbd", fontSize: 18 }}>
                          {selectedBalance?.available
                            ? `${fmt(selectedBalance.balance, selectedType.decimals)} ${selectedType.unitLabel}`
                            : `0 ${selectedType.unitLabel}`}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,.38)" }}>
                          {selectedBalance?.field
                            ? `${L(language, "Field", "Campo")}: ${selectedBalance.field}`
                            : L(
                                language,
                                "Resource not available in this account",
                                "Recurso no disponible en esta cuenta",
                              )}
                        </Typography>
                      </Box>
                    </Stack>
                  </GlassPanel>
                ) : null}

                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label={L(language, "Total amount", "Cantidad total")}
                      value={totalAmount}
                      onChange={(event) => setTotalAmount(event.target.value)}
                      disabled={busy}
                      helperText={L(language, "Total locked from your account", "Total bloqueado de tu cuenta")}
                      FormHelperTextProps={{ sx: { color: "rgba(255,255,255,.45)" } }}
                      sx={{
                        input: { color: "white", fontWeight: 900 },
                        label: { color: "rgba(255,255,255,.65)" },
                        "& .MuiOutlinedInput-root fieldset": {
                          borderColor: "rgba(255,255,255,.25)",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={L(language, "Duration", "Duración")}
                      value={duration}
                      onChange={(event) => setDuration(event.target.value)}
                      disabled={busy}
                      helperText={L(language, "Examples: 10m, 1h, 1d", "Ejemplos: 10m, 1h, 1d")}
                      FormHelperTextProps={{ sx: { color: "rgba(255,255,255,.45)" } }}
                      sx={{
                        input: { color: "white", fontWeight: 900 },
                        label: { color: "rgba(255,255,255,.65)" },
                        "& .MuiOutlinedInput-root fieldset": {
                          borderColor: "rgba(255,255,255,.25)",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label={L(language, "Max participants (optional)", "Máximo de participantes (opcional)")}
                      value={maxParticipants}
                      onChange={(event) => setMaxParticipants(event.target.value)}
                      disabled={busy}
                      helperText={L(language, "Whole items are protected automatically", "Los artículos enteros se protegen automáticamente")}
                      FormHelperTextProps={{ sx: { color: "rgba(255,255,255,.45)" } }}
                      sx={{
                        input: { color: "white", fontWeight: 900 },
                        label: { color: "rgba(255,255,255,.65)" },
                        "& .MuiOutlinedInput-root fieldset": {
                          borderColor: "rgba(255,255,255,.25)",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={L(language, "Message", "Mensaje")}
                      value={message}
                      onChange={(event) => setMessage(event.target.value)}
                      disabled={busy}
                      inputProps={{ maxLength: 140 }}
                      helperText={L(language, "Optional message", "Mensaje opcional")}
                      FormHelperTextProps={{ sx: { color: "rgba(255,255,255,.45)" } }}
                      sx={{
                        input: { color: "white", fontWeight: 900 },
                        label: { color: "rgba(255,255,255,.65)" },
                        "& .MuiOutlinedInput-root fieldset": {
                          borderColor: "rgba(255,255,255,.25)",
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                {estimatedSplits ? (
                  <Box
                    sx={{
                      mt: 1.2,
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(3, minmax(0, 1fr))",
                      },
                      gap: 0.8,
                    }}
                  >
                    <MetricBox
                      label={L(language, "EST. EACH", "EST. POR PERSONA")}
                      value={`${fmt(estimatedSplits.each, selectedType?.decimals || 0)} ${
                        selectedType?.unitLabel || ""
                      }`}
                      accent="#73ffbd"
                    />
                    <MetricBox
                      label={L(language, "REMAINDER", "RESTANTE")}
                      value={`${fmt(estimatedSplits.remainder, selectedType?.decimals || 0)} ${
                        selectedType?.unitLabel || ""
                      }`}
                      accent="#ffcf66"
                    />
                    <MetricBox
                      label={L(language, "SOURCE", "ORIGEN")}
                      value={selectedType?.source === "city" ? "CITY FARM" : "BIG DOG BONE"}
                      accent="#79e8ff"
                    />
                  </Box>
                ) : null}

                <Button
                  fullWidth
                  variant="contained"
                  onClick={createDrop}
                  disabled={busy || !rewardType || !rewardTypes.length}
                  sx={{
                    mt: 1.5,
                    py: 1.25,
                    color: "#160900",
                    fontWeight: 1000,
                    background: "linear-gradient(90deg,#fff071,#ff9b00)",
                  }}
                >
                  {busy ? L(language, "PROCESSING...", "PROCESANDO...") : L(language, "CREATE AIRDROP", "CREAR AIRDROP")}
                </Button>

                <Divider sx={{ my: 1.5, borderColor: "rgba(255,255,255,.08)" }} />

                <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 12, lineHeight: 1.6 }}>
                  {L(language, "Min duration", "Duración mínima")}:{" "}
                  {durationHelper(status?.config?.minDurationMs, language)} ·{" "}
                  {L(language, "Max duration", "Duración máxima")}:{" "}
                  {durationHelper(status?.config?.maxDurationMs, language)}
                </Typography>
              </GlassPanel>
            </Grid>

            <Grid item xs={12} lg={7.3}>
              <GlassPanel sx={{ p: 2, minHeight: 430 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                  sx={{ mb: 1.2 }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 1000, color: "#73ffbd", fontSize: 19 }}>
                      {L(language, "🔥 ACTIVE NOW", "🔥 ACTIVOS AHORA")}
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 12 }}>
                      {L(
                        language,
                        "Join now. Payment is made automatically when the timer ends.",
                        "Únete ahora. El pago se realiza automáticamente cuando termina el temporizador.",
                      )}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={0.8}>
                    <Chip
                      label={`${activeCount} ${L(language, "ACTIVE", "ACTIVOS")}`}
                      sx={{
                        color: "#00170d",
                        bgcolor: "#73ffbd",
                        fontWeight: 1000,
                      }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={settleExpired}
                      disabled={busy}
                      sx={{
                        color: "#ffd45d",
                        borderColor: "rgba(255,211,78,.45)",
                        fontWeight: 900,
                      }}
                    >
                      {L(language, "SETTLE", "LIQUIDAR")}
                    </Button>
                  </Stack>
                </Stack>

                {loading ? (
                  <Stack alignItems="center" spacing={1.5} sx={{ py: 7 }}>
                    <CircularProgress sx={{ color: "#ffd45d" }} />
                    <Typography sx={{ color: "rgba(255,255,255,.65)" }}>
                      {L(language, "Loading airdrops...", "Cargando airdrops...")}
                    </Typography>
                  </Stack>
                ) : active.length ? (
                  <Stack spacing={1}>
                    {active.map((drop) => (
                      <AirdropActiveCard
                        key={drop.airdropId}
                        drop={{
                          ...drop,
                          viewerName: status?.user?.nombre || "",
                        }}
                        busy={busy}
                        onJoin={joinDrop}
                        onCancel={cancelDrop}
                        language={language}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Box
                    sx={{
                      py: 8,
                      textAlign: "center",
                      border: "1px dashed rgba(255,255,255,.16)",
                      borderRadius: "18px",
                      bgcolor: "rgba(255,255,255,.035)",
                    }}
                  >
                    <Typography sx={{ fontSize: 42 }}>🎁</Typography>
                    <Typography sx={{ fontWeight: 1000, color: "#ffd45d" }}>
                      {L(language, "No active airdrops right now", "No hay airdrops activos ahora")}
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 13 }}>
                      {L(
                        language,
                        "Be the first to create one for the community.",
                        "Sé el primero en crear uno para la comunidad.",
                      )}
                    </Typography>
                  </Box>
                )}
              </GlassPanel>
            </Grid>

            <Grid item xs={12}>
              <GlassPanel
                sx={{
                  p: 2,
                  borderColor: "rgba(115,255,189,.22)",
                  background:
                    "linear-gradient(145deg,rgba(115,255,189,.07),rgba(5,3,10,.88))",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mb: 1.2 }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 1000, color: "#73ffbd", fontSize: 18 }}>
                      {L(language, "✅ PAYMENT HISTORY", "✅ HISTORIAL DE PAGOS")}
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 12 }}>
                      {L(
                        language,
                        "Completed, cancelled and refunded drops appear here.",
                        "Los airdrops completados, cancelados y reembolsados aparecen aquí.",
                      )}
                    </Typography>
                  </Box>

                  <Chip
                    label={`${history.length} ${L(language, "RECENT", "RECIENTES")}`}
                    sx={{
                      color: "#00170d",
                      bgcolor: "#73ffbd",
                      fontWeight: 1000,
                      alignSelf: { xs: "flex-start", sm: "center" },
                    }}
                  />
                </Stack>

                {history.length ? (
                  <Grid container spacing={1}>
                    {history.map((drop) => (
                      <Grid key={drop.airdropId} item xs={12} md={6}>
                        <DropHistoryCard
                          drop={drop}
                          onOpenReceipt={loadReceipt}
                          language={language}
                        />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box
                    sx={{
                      py: 3.5,
                      textAlign: "center",
                      border: "1px dashed rgba(255,255,255,.14)",
                      borderRadius: "18px",
                      bgcolor: "rgba(255,255,255,.03)",
                    }}
                  >
                    <Typography sx={{ fontSize: 32 }}>📜</Typography>
                    <Typography sx={{ fontWeight: 1000, color: "#ffd45d" }}>
                      {L(language, "No payment history yet", "Todavía no hay historial de pagos")}
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 13 }}>
                      {L(
                        language,
                        "When an airdrop expires and is settled, it will show here.",
                        "Cuando un airdrop vence y se liquida, aparecerá aquí.",
                      )}
                    </Typography>
                  </Box>
                )}
              </GlassPanel>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      <AirdropHelpDialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        language={language}
      />

      <AirdropReceiptDialog
        open={receiptOpen}
        receipt={selectedReceipt}
        loading={receiptLoading}
        onClose={() => setReceiptOpen(false)}
        language={language}
      />

      <Snackbar
        open={internalNotice.open}
        autoHideDuration={4200}
        onClose={() =>
          setInternalNotice((current) => ({
            ...current,
            open: false,
          }))
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={internalNotice.type}
          onClose={() =>
            setInternalNotice((current) => ({
              ...current,
              open: false,
            }))
          }
          sx={{ fontWeight: 850 }}
        >
          {internalNotice.text}
        </Alert>
      </Snackbar>
    </>
  );
}
